import React, { useEffect, useState } from "react";
import { HiOutlineCreditCard } from "react-icons/hi";
import TimesheetStatusChips from "../../components/TimesheetRevamp/TimesheetStatusChips";
import {
  JOB_STATUS,
  formatRunCost,
  formatRunDuration,
  runElapsedSeconds,
} from "../model/bulkAnalyseModel";
import { useBulkAnalyse } from "../BulkAnalyseContext";
import BulkAnalyseKpi from "./BulkAnalyseKpi";
import { BulkAnalyseTable } from "./BulkAnalyseTable";
import {
  BulkAnalyseFooter,
  BulkAnalyseModalChrome,
  BulkPrimaryButton,
  BulkSecondaryButton,
} from "./BulkAnalyseModalChrome";

const FILTERS = [
  { value: "all", label: "All" },
  { value: "done", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "queued", label: "Queued" },
  { value: "running", label: "Running" },
  { value: "stopped", label: "Paused" },
];

function useRunClock(job) {
  const running = job?.status === JOB_STATUS.RUNNING;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running, job?.uuid]);

  return formatRunDuration(runElapsedSeconds(job, running ? now : Date.now()));
}

const BulkAnalyseJobModal = () => {
  const {
    job,
    jobOpen,
    closeJob,
    counts,
    resumeAllowed,
    resumeTotal,
    retryAllowed,
    retryTotal,
    stopLabel,
    dateLabel,
    balance,
    haltRun,
    resume,
    retry,
    stopLoading,
    stopError,
    retryLoading,
    jobFilter,
    changeJobFilter,
    jobListPage,
    jobPage,
    setJobPage,
    itemsLoading,
    onOpenReport,
  } = useBulkAnalyse();

  const elapsedLabel = useRunClock(job);

  if (!job) return null;

  const running = job.status === JOB_STATUS.RUNNING;
  const filters = FILTERS;
  const dates = dateLabel || "";
  const subtitle = running
    ? (dates ? `${dates} · still analysing` : "Still analysing")
    : stopLabel
      ? (dates ? `${dates} · ${stopLabel.toLowerCase()}` : stopLabel)
      : (dates ? `${dates} · completed` : "Completed");
  const title = running
    ? "Bulk Analyse"
    : counts.failed
      ? `${counts.failed} failed · ${counts.done} saved`
      : counts.stopped
        ? "Paused"
        : "Bulk Analyse";

  return (
    <BulkAnalyseModalChrome
      isOpen={jobOpen}
      onClose={closeJob}
      title={title}
      subtitle={subtitle}
      wide
      panelClassName="!w-[75vw] !max-w-[75vw] rounded-2xl"
      footer={
        <BulkAnalyseFooter>
          <p className="min-w-0 text-[13px] font-medium leading-snug text-[#64748B] lg:flex-1">
            A credit is taken only when a timesheet finishes. Closing this leaves the job as it is.
          </p>
          <div className="flex w-full flex-wrap items-center justify-end gap-2 lg:w-auto lg:shrink-0">
            {/* {balance != null ? (
              <span className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#ECF0FD] px-3 text-sm font-medium text-[#02397A]">
                <HiOutlineCreditCard className="h-4 w-4 text-[#4B6CB7]" />
                Balance
                <span className="font-bold">${Number(balance).toFixed(2)}</span>
              </span>
            ) : null} */}
            <BulkSecondaryButton onClick={closeJob}>Close</BulkSecondaryButton>
            {running ? (
              <BulkPrimaryButton onClick={haltRun} disabled={stopLoading}>
                {stopLoading ? "Pausing…" : "Pause"}
              </BulkPrimaryButton>
            ) : null}
            {resumeAllowed ? (
              <BulkPrimaryButton onClick={resume} disabled={retryLoading}>
                {retryLoading ? "Resuming…" : `Resume ${resumeTotal}`}
              </BulkPrimaryButton>
            ) : null}
            {retryAllowed ? (
              <BulkPrimaryButton onClick={retry} disabled={retryLoading}>
                {retryLoading ? "Retrying…" : `Retry ${retryTotal}`}
              </BulkPrimaryButton>
            ) : null}
          </div>
        </BulkAnalyseFooter>
      }
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-hidden px-4 py-3 sm:gap-4 sm:px-6 sm:py-4">
        {stopError ? (
          <p
            role="alert"
            className="shrink-0 rounded-lg border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-[13px] font-medium text-[#B91C1C]"
          >
            {stopError}
          </p>
        ) : null}
        <div className="grid shrink-0 grid-cols-2 gap-2 xl:grid-cols-5">
          <BulkAnalyseKpi
            compact
            label="Timesheets saved"
            value={counts.done}
            caption="Completed in this run"
            accent="bg-[#059669]"
            loading={itemsLoading && !counts.total}
          />
          <BulkAnalyseKpi
            compact
            label="Timesheets failed"
            value={counts.failed}
            caption={counts.failed ? "Can be retried" : "Nothing failed"}
            accent="bg-[#FC466C]"
            loading={itemsLoading && !counts.total}
          />
          <BulkAnalyseKpi
            compact
            label="Still to run"
            value={counts.remaining}
            caption={running ? "Waiting in the queue" : "Nothing waiting"}
            accent="bg-[#4B6CB7]"
            loading={itemsLoading && !counts.total}
          />
          <BulkAnalyseKpi
            compact
            label="Cost of this run"
            value={formatRunCost(job.cost_aud)}
            caption={running ? "So far" : "For timesheets that finished"}
            accent="bg-[#02397A]"
            loading={itemsLoading && job.cost_aud == null}
          />
          <BulkAnalyseKpi
            compact
            label="Time taken"
            value={elapsedLabel}
            caption={running ? "Since it started" : "Start to finish"}
            accent="bg-[#33416B]"
            loading={itemsLoading && !job.created_at}
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="mb-2 flex shrink-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <h3 className="min-w-0 text-[13px] font-semibold text-[#0F172A]">
              Filter the timesheets in this Bulk Analyse.
            </h3>
            {/* <p className="text-xs font-medium text-[#64748B]">{counts.total} in this run</p> */}
          </div>
          <div className="mb-3 flex shrink-0 flex-wrap items-center gap-2">
            <TimesheetStatusChips
              options={filters}
              selectedValue={jobFilter || "all"}
              onChange={(option) => changeJobFilter(option.value)}
            />
            {/* <p className="basis-full text-xs font-medium leading-snug text-[#64748B] lg:basis-auto lg:flex-1">
              Left out: already analysed, so not sent again.
            </p> */}
          </div>
          <BulkAnalyseTable
            className="min-h-0 flex-1"
            rows={jobListPage.rows}
            page={jobPage}
            totalPages={jobListPage.totalPages}
            total={jobListPage.total}
            onPageChange={setJobPage}
            onOpenReport={onOpenReport}
            showRunStatus
            loading={itemsLoading && jobListPage.rows.length === 0}
          />
        </div>
      </div>
    </BulkAnalyseModalChrome>
  );
};

export default BulkAnalyseJobModal;
